/**
 * Utility for compressing and resizing profile photos
 */

/**
 * Compress and resize an image to 64x64 pixels
 * @param blob - The image blob from Graph API
 * @returns Promise<string> - Base64 encoded compressed image
 */
export async function compressProfilePhoto(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();

            img.onload = () => {
                // Create canvas for resizing
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                if (!ctx) {
                    reject(new Error("Failed to get canvas context"));
                    return;
                }

                // Set dimensions to 64x64
                const size = 64;
                canvas.width = size;
                canvas.height = size;

                // Calculate cropping to maintain aspect ratio (center crop)
                let sourceX = 0;
                let sourceY = 0;
                let sourceWidth = img.width;
                let sourceHeight = img.height;

                const aspectRatio = img.width / img.height;

                if (aspectRatio > 1) {
                    // Image is wider than tall
                    sourceX = (img.width - img.height) / 2;
                    sourceWidth = img.height;
                } else if (aspectRatio < 1) {
                    // Image is taller than wide
                    sourceY = (img.height - img.width) / 2;
                    sourceHeight = img.width;
                }

                // Draw and resize image
                ctx.drawImage(
                    img,
                    sourceX,
                    sourceY,
                    sourceWidth,
                    sourceHeight,
                    0,
                    0,
                    size,
                    size
                );

                // Convert to base64 with compression
                // Using JPEG for better compression (quality 0.8 for balance)
                const base64 = canvas.toDataURL("image/jpeg", 0.8);
                resolve(base64);
            };

            img.onerror = () => {
                reject(new Error("Failed to load image"));
            };

            img.src = e.target?.result as string;
        };

        reader.onerror = () => {
            reject(new Error("Failed to read blob"));
        };

        reader.readAsDataURL(blob);
    });
}

/**
 * Check if a string is a valid base64 image
 * @param str - The string to check
 * @returns boolean
 */
export function isValidBase64Image(str: string): boolean {
    if (!str) return false;

    // Check for data URL format
    const dataUrlRegex = /^data:image\/(jpeg|jpg|png|gif|webp);base64,/;
    return dataUrlRegex.test(str);
}

/**
 * Extract base64 data from data URL
 * @param dataUrl - The data URL string
 * @returns string - Just the base64 data
 */
export function extractBase64Data(dataUrl: string): string {
    const base64Index = dataUrl.indexOf("base64,");
    if (base64Index === -1) return dataUrl;
    return dataUrl.substring(base64Index + 7);
}
